import Dialog from "@mui/material/Dialog";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import Button from "@mui/material/Button";
import Divider from "@mui/material/Divider";
import { useEffect } from "react";
import type { Artwork, Collection } from "../types";
import { STATUS_LABEL } from "../types";
import { artImage } from "../utils/image";
import { tokens } from "../theme";
import { INQUIRY_EMAIL } from "../auth";

const CloseIcon = () => <Box component="span">×</Box>;
const ChevronLeft = () => <Box component="span">‹</Box>;
const ChevronRight = () => <Box component="span">›</Box>;

function Detail({ label, value }: { label: string; value?: string }) {
  if (!value) return null;
  return (
    <Box sx={{ display: "flex", gap: 2, py: 0.6 }}>
      <Typography
        sx={{
          width: 96,
          flexShrink: 0,
          fontSize: "0.68rem",
          fontWeight: 700,
          letterSpacing: "0.16em",
          textTransform: "uppercase",
          color: tokens.taupe,
          pt: "2px",
        }}
      >
        {label}
      </Typography>
      <Typography sx={{ fontSize: "0.95rem", color: tokens.ink }}>
        {value}
      </Typography>
    </Box>
  );
}

export default function ArtworkLightbox({
  art,
  collection,
  onClose,
  onPrev,
  onNext,
}: {
  art: Artwork | null;
  collection?: Collection;
  onClose: () => void;
  onPrev?: () => void;
  onNext?: () => void;
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "ArrowLeft") onPrev?.();
      if (e.key === "ArrowRight") onNext?.();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onPrev, onNext]);

  const open = !!art;
  const mailto = art
    ? `mailto:${INQUIRY_EMAIL}?subject=${encodeURIComponent(`Inquiry: "${art.title}"`)}&body=${encodeURIComponent(`Hi, I'd love to know more about "${art.title}"${art.dimensions ? ` (${art.dimensions})` : ""}.`)}`
    : "#";

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: { bgcolor: tokens.bone, m: { xs: 1, md: 3 }, overflow: "hidden" },
      }}
    >
      {art && (
        <Box
          sx={{
            position: "relative",
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "1.35fr 1fr" },
          }}
        >
          <IconButton
            onClick={onClose}
            aria-label="Close"
            sx={{
              position: "absolute",
              top: 8,
              right: 8,
              zIndex: 3,
              color: tokens.ink,
              bgcolor: "rgba(250,247,241,0.7)",
              "&:hover": { bgcolor: "rgba(250,247,241,0.95)" },
            }}
          >
            <CloseIcon />
          </IconButton>

          <Box
            sx={{
              position: "relative",
              bgcolor: "#efe9df",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              p: { xs: 2, md: 4 },
              minHeight: { md: 540 },
            }}
          >
            <Box
              component="img"
              src={artImage(art)}
              alt={art.title}
              sx={{
                maxWidth: "100%",
                maxHeight: { xs: "52vh", md: "78vh" },
                objectFit: "contain",
                boxShadow: "0 18px 50px rgba(27,26,23,0.22)",
              }}
            />
            {onPrev && (
              <IconButton
                onClick={onPrev}
                aria-label="Previous"
                sx={{
                  position: "absolute",
                  left: 6,
                  top: "50%",
                  transform: "translateY(-50%)",
                  color: tokens.ink,
                  bgcolor: "rgba(250,247,241,0.6)",
                  "&:hover": { bgcolor: "rgba(250,247,241,0.9)" },
                }}
              >
                <ChevronLeft />
              </IconButton>
            )}
            {onNext && (
              <IconButton
                onClick={onNext}
                aria-label="Next"
                sx={{
                  position: "absolute",
                  right: 6,
                  top: "50%",
                  transform: "translateY(-50%)",
                  color: tokens.ink,
                  bgcolor: "rgba(250,247,241,0.6)",
                  "&:hover": { bgcolor: "rgba(250,247,241,0.9)" },
                }}
              >
                <ChevronRight />
              </IconButton>
            )}
          </Box>

          <Box
            sx={{
              p: { xs: 3, md: 5 },
              display: "flex",
              flexDirection: "column",
            }}
          >
            {collection && (
              <Typography
                sx={{
                  fontSize: "0.68rem",
                  fontWeight: 700,
                  letterSpacing: "0.18em",
                  textTransform: "uppercase",
                  color: tokens.brass,
                  mb: 1.2,
                }}
              >
                {collection.name}
              </Typography>
            )}
            <Typography
              sx={{
                fontFamily: tokens.display,
                fontSize: { xs: "2rem", md: "2.6rem" },
                lineHeight: 1.04,
                color: tokens.ink,
              }}
            >
              {art.title}
            </Typography>
            {art.year && (
              <Typography
                sx={{
                  fontFamily: tokens.display,
                  fontStyle: "italic",
                  fontSize: "1.05rem",
                  color: tokens.taupe,
                  mt: 0.5,
                }}
              >
                {art.year}
              </Typography>
            )}

            <Divider sx={{ my: 2.5 }} />
            <Detail label="Medium" value={art.medium} />
            <Detail label="Size" value={art.dimensions} />
            <Detail label="Gallery" value={art.galleryAttribution} />
            <Detail label="Status" value={STATUS_LABEL[art.status]} />
            {art.status === "available" && (
              <Detail label="Price" value={art.price} />
            )}

            {art.description && (
              <Typography
                sx={{ mt: 2.5, fontSize: "0.98rem", lineHeight: 1.72 }}
              >
                {art.description}
              </Typography>
            )}

            <Box sx={{ flex: 1 }} />
            <Box sx={{ mt: 4 }}>
              {art.status === "available" ? (
                <Button
                  component="a"
                  href={mailto}
                  variant="contained"
                  color="primary"
                  fullWidth
                  sx={{ py: 1.3 }}
                >
                  Inquire about this piece
                </Button>
              ) : (
                <Button disabled variant="outlined" fullWidth sx={{ py: 1.3 }}>
                  {art.status === "sold" ? "Sold" : "Not for sale"}
                </Button>
              )}
            </Box>
          </Box>
        </Box>
      )}
    </Dialog>
  );
}
